import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';
import { authUtils } from '../utils/auth';
import { cookieUtils } from '../utils/cookies';
import Swal from 'sweetalert2';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // Check authentication status on app start
        const checkAuthStatus = () => {
            const authenticated = authUtils.isAuthenticated();
            setIsAuthenticated(authenticated);

            if (authenticated) {
                const userData = authUtils.getCurrentUser();
                setUser(userData);
            } else {
                // If token expired, clear user data
                setUser(null);
                authUtils.clearCachedUser();
            }

            setIsLoading(false);
        };

        checkAuthStatus();

        // Listen for auth state changes (login/logout)
        const handleAuthChange = () => {
            checkAuthStatus();
        };

        window.addEventListener('auth:stateChanged', handleAuthChange);

        // Set up periodic token validation (every 5 minutes)
        const tokenValidationInterval = setInterval(() => {
            const authenticated = authUtils.isAuthenticated();
            if (!authenticated && isAuthenticated) {
                // Token expired, show notification and redirect to login
                Swal.fire({
                    icon: 'warning',
                    title: 'Session Expired',
                    text: 'Your session has expired. Please log in again.',
                    confirmButtonText: 'Go to Login',
                    confirmButtonColor: '#10B981',
                    allowOutsideClick: false,
                    customClass: {
                        confirmButton: 'swal-confirm-button'
                    },
                    didOpen: () => {
                        const swal = Swal.getPopup();
                        if (swal) {
                            swal.style.zIndex = '99999';
                        }
                    }
                }).then(() => {
                    window.location.href = '/login';
                });
            }
        }, 5 * 60 * 1000); // Check every 5 minutes

        return () => {
            window.removeEventListener('auth:stateChanged', handleAuthChange);
            clearInterval(tokenValidationInterval);
        };
    }, [isAuthenticated]);

    const login = (userData) => {
        setUser(userData);
        setIsAuthenticated(true);
        // Trigger auth state change event
        window.dispatchEvent(new Event('auth:stateChanged'));
    };

    const logout = async () => {
        // Clear authentication data first
        setUser(null);
        setIsAuthenticated(false);
        authUtils.clearCachedUser();

        // Clear cookies and localStorage
        cookieUtils.clearAuthCookies();
        localStorage.removeItem('user');

        // Call API to revoke tokens (optional, but good practice)
        try {
            const refreshToken = cookieUtils.getRefreshToken();
            if (refreshToken) {
                await authAPI.logout(refreshToken);
            }
        } catch (error) {
            console.error('Logout API call failed:', error);
        }

        // Set flag to show logout confirmation on home page
        sessionStorage.setItem('justLoggedOut', 'true');

        // Trigger auth state change event for UI updates
        window.dispatchEvent(new Event('auth:stateChanged'));

        // Redirect to home page
        window.location.href = '/';
    };

    const updateUser = (updatedUserData) => {
        setUser(updatedUserData);
    };

    const value = {
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        updateUser
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
